import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Hexagon, Scale } from 'lucide-react';



// ───────────── ANIMATION HELPERS ─────────────

// Wraps each character of `text` in a span so they can stagger-cascade in.
const CascadeText = ({ text, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: false, amount: 0.5 });
  return (
    <motion.span
      ref={ref}
      className={`inline-block ${className}`}
      initial="hidden"
      animate={inView ? 'show' : 'hidden'}
      variants={{
        hidden: {},
        show:   { transition: { staggerChildren: 0.04, delayChildren: 0.1 } },
      }}
      aria-label={text}
    >
      {text.split('').map((ch, i) => (
        <motion.span
          key={i}
          aria-hidden
          className="inline-block"
          variants={{
            hidden: { opacity: 0, y: -10, filter: 'blur(4px)' },
            show:   { opacity: 1, y: 0,   filter: 'blur(0px)', transition: { type: 'spring', stiffness: 300, damping: 18 } },
          }}
        >
          {ch === ' ' ? '\u00A0' : ch}
        </motion.span>
      ))}
    </motion.span>
  );
};


// Custom Aerodynamics Icon — three wind streams flowing past a paddle silhouette
const AerodynamicsIcon = ({ className }) => (
  <svg
    viewBox="0 0 32 32"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`aerodynamics-svg ${className || ''}`}
  >
    {/* Paddle silhouette (faint, in the background) */}
    <g opacity="0.55" className="aero-paddle">
      <path d="M16 4 C 21 4 23 7 23 12 L 23 17 C 23 19 22 20 21 21 L 18 27 L 14 27 L 11 21 C 10 20 9 19 9 17 L 9 12 C 9 7 11 4 16 4 Z" />
    </g>
    {/* Three wind streams — these animate independently to look like flowing air */}
    <path className="aero-stream aero-stream-1" d="M2 9 Q 8 9 14 9" />
    <path className="aero-stream aero-stream-2" d="M2 16 Q 9 16 16 16" />
    <path className="aero-stream aero-stream-3" d="M2 23 Q 7 23 12 23" />
  </svg>
);

// Custom Tapered Throat Icon Component - Version 2
const TaperedThroatIcon = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    {/* Left side of paddle with taper */}
    <path d="M8 3 Q6 3 6 5 L6 11 Q6 13 7 14 L9.5 18.5 L9.5 21" />
    {/* Right side of paddle with taper */}
    <path d="M16 3 Q18 3 18 5 L18 11 Q18 13 17 14 L14.5 18.5 L14.5 21" />
    {/* Top of paddle */}
    <path d="M8 3 L16 3" />
    {/* Handle bottom */}
    <path d="M9.5 21 L14.5 21" />
    {/* Emphasis lines showing taper */}
    <path d="M9 11 L10.5 16" strokeWidth="1.5" opacity="0.6" />
    <path d="M15 11 L13.5 16" strokeWidth="1.5" opacity="0.6" />
  </svg>
);

// Custom Foam Edges Icon Component - Side view of paddle showing foam layers
const FoamEdgesIcon = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    {/* Paddle outline - side view */}
    <rect x="5" y="4" width="14" height="16" rx="2" />
    {/* Inner foam layer */}
    <rect x="7" y="6" width="10" height="12" rx="1" opacity="0.4" />
    {/* Core layer */}
    <rect x="9" y="8" width="6" height="8" rx="0.5" opacity="0.3" />
    {/* Reinforced edge indicators (dots/circles at corners) */}
    <circle cx="5" cy="4" r="1.5" fill="currentColor" />
    <circle cx="19" cy="4" r="1.5" fill="currentColor" />
    <circle cx="5" cy="20" r="1.5" fill="currentColor" />
    <circle cx="19" cy="20" r="1.5" fill="currentColor" />
    {/* Edge emphasis lines */}
    <path d="M5 8 L7 8" strokeWidth="2.5" />
    <path d="M5 12 L7 12" strokeWidth="2.5" />
    <path d="M5 16 L7 16" strokeWidth="2.5" />
    <path d="M17 8 L19 8" strokeWidth="2.5" />
    <path d="M17 12 L19 12" strokeWidth="2.5" />
    <path d="M17 16 L19 16" strokeWidth="2.5" />
  </svg>
);

const techFeatures = [
  {
    icon: AerodynamicsIcon,
    title: 'Aerodynamics',
    description: 'Streamlined edges and tapered throat design reduce drag for faster swings and quicker transitions between shots.',
    color: 'text-[#E02128]',
    bgColor: 'bg-[#F4F4F5]',
    animation: 'icon-aero'
  },
  {
    icon: Hexagon,
    title: 'HexCore Technology',
    description: 'Honeycomb lattice structure provides responsive power and stability while maintaining a lighter feel for reduced fatigue.',
    color: 'text-[#E02128]',
    bgColor: 'bg-[#F4F4F5]',
    animation: 'icon-pulse'
  },
  {
    icon: TaperedThroatIcon,
    title: 'All New Throat Design',
    description: 'Tapered "V" throat design on bottom face providing balanced throttle, enhanced stability, smoother power transfer, and improved control during high-speed swings.',
    color: 'text-[#E02128]',
    bgColor: 'bg-[#F4F4F5]',
    animation: 'icon-bounce'
  },
  {
    icon: Scale,
    title: 'Weight Distribution',
    description: 'Strategically positioned weight at the handle base ensures optimal weight distribution, reducing fatigue and minimizing wrist strain during extended play.',
    color: 'text-[#E02128]',
    bgColor: 'bg-[#F4F4F5]',
    animation: 'icon-swing'
  },
  {
    icon: FoamEdgesIcon,
    title: 'Steady Foam Edges',
    description: 'The foam edges are reinforced with higher-density foam to improve overall structural integrity. This added firmness at the perimeter helps distribute frictional forces more evenly across the surface.',
    color: 'text-[#E02128]',
    bgColor: 'bg-[#F4F4F5]',
    animation: 'icon-glow-pulse'
  }
];

export const TechnologySection = () => {
  const statsRef = useRef(null);
  const [counts, setCounts] = useState({ stat1: 3, stat2: 40, stat3: 2 });
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            
            // Add animate class for CSS animations
            const statsElements = entry.target.querySelectorAll('.stats-animate');
            statsElements.forEach((el) => {
              el.classList.add('animate');
            });

            // Counter animation
            const duration = 2000;
            const steps = 60;
            let step = 0;
            const timer = setInterval(() => {
              step++;
              const progress = step / steps;
              const eased = 1 - Math.pow(1 - progress, 3); // Ease out cubic
              
              setCounts({
                stat1: Math.round(3 * eased),
                stat2: Math.round(40 * eased),
                stat3: Math.round(2 * eased),
              });
              
              if (step >= steps) clearInterval(timer);
            }, duration / steps);
          }
        });
      },
      { threshold: 0.5, rootMargin: '-10% 0px -10% 0px' }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  return (
    <section className="relative py-12 md:py-16 bg-[#F9F9FA] border-t-2 border-[#E02128] overflow-hidden" id="technology" aria-label="DYSSC Pickleball Paddle Technology">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Stats Section - Always 3 columns */}
        <div ref={statsRef} className="grid grid-cols-3 gap-2 md:gap-8 mb-10 md:mb-12 pt-4 md:pt-0">
          <div className="text-center stats-animate">
            <p className="font-display text-4xl sm:text-5xl md:text-7xl lg:text-8xl text-[#E02128] mb-2 md:mb-4">{counts.stat1}X</p>
            <p className="text-[#52525B] text-[10px] sm:text-xs md:text-base tracking-[0.1em] md:tracking-[0.2em] uppercase">Faster Swaps</p>
          </div>
          <div className="text-center stats-animate stats-delay-1 border-x border-[#E4E4E7]">
            <p className="font-display text-4xl sm:text-5xl md:text-7xl lg:text-8xl text-[#E02128] mb-2 md:mb-4">{counts.stat2}%</p>
            <p className="text-[#52525B] text-[10px] sm:text-xs md:text-base tracking-[0.1em] md:tracking-[0.2em] uppercase">Less Fatigue</p>
          </div>
          <div className="text-center stats-animate stats-delay-2">
            <p className="font-display text-4xl sm:text-5xl md:text-7xl lg:text-8xl text-[#E02128] mb-2 md:mb-4">{counts.stat3}X</p>
            <p className="text-[#52525B] text-[10px] sm:text-xs md:text-base tracking-[0.1em] md:tracking-[0.2em] uppercase">Better Control</p>
          </div>
        </div>

        <div className="text-center mb-10">
          <p className="text-xs tracking-[0.2em] uppercase text-[#E02128] font-bold mb-4">
            Engineering Excellence
          </p>
          <h2 className="font-display text-4xl md:text-5xl text-[#111214] mb-4">
            <span className="neon-underline">TECHNOLOGY THAT PERFORMS</span>
          </h2>
          <p className="text-[#52525B] text-lg max-w-3xl mx-auto">
            Every curve, every layer, every gram engineered for one purpose: to give you the edge on the court.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {techFeatures.map((feature, index) => (
            <div key={index} className="group">
              <div className="relative">
                <div className="flex items-center gap-4 mb-3">
                  <div className={`w-12 h-12 rounded-lg ${feature.bgColor} flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-110 ${feature.animation}`}>
                    <feature.icon className={`w-6 h-6 ${feature.color}`} />
                  </div>

                  <h3 className="font-heading text-lg md:text-xl font-semibold text-[#111214] leading-tight">
                    {feature.title}
                  </h3>
                </div>

                <p className="text-[#52525B] leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
